import {
  handleCreateShipmentLabel,
  handleClinicRmaShipmentReminder,
  useRmaStore,
} from '@/entities/rma';
import { useSetState } from '@/shared/lib/customHooks';
import { showMessageError, showMessageSuccess } from '@/shared/lib';
import _ from 'lodash';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'ttag';
import type { PreviewShipmentModalProps } from './previewShipmentModal.types';

/**
 * State and actions of the shipment preview modal that buys a shipping label for an RMA or a patient return. Exposes an isSaving flag, an onSubmit that creates the label and navigates to the related record, and a memoized selectedRateData that is the already chosen rate for a shipping label or the rate matching data.rateId merged with the configured parcel's name and weight from the RMA store's patientReturnConfig.
 *
 * @param props - Preview data, rates and the isShippingLabel discriminator.
 * @returns Saving state, the purchase handler and the resolved rate data.
 */
export const usePreviewShipmentModal = (props: PreviewShipmentModalProps) => {
  const navigate = useNavigate();

  /**
   * The patient return configuration held in the RMA store. Read through a useRmaStore selector so the hook re-renders whenever the stored configuration changes.
   *
   * @returns The stored patientReturnConfig value.
   */
  const patientReturnConfig = useRmaStore((s) => s.patientReturnConfig);

  const [state, setState] = useSetState({
    isSaving: false,
  });

  /**
   * Purchases the previewed shipping label and routes to the related record. Builds an Inbound RMA input including the selected devices not yet on a label, or an Outbound patient-return input, calls handleCreateShipmentLabel, turns off the clinic shipment reminder when one is active, navigates to the RMA or study detail page, and on failure logs the error, shows it and clears isSaving.
   *
   * @returns A promise resolving once the label has been purchased and the navigation triggered.
   */
  const onSubmit = async () => {
    try {
      setState({ isSaving: true });

      const { data } = props;

      if (props.isShippingLabel) {
        const shipmentDevices = data.notInLabelDeviceObjects
          .filter((device: any) => data.selectedDevices.includes(device.deviceId))
          .map((device: any) => device.id);
        const input = {
          ticketId: data.rmaTicket.id,
          ticketType: 'RMA',
          rateId: data.selectedRate.id,
          type: 'Inbound',
          senderContact: data.senderContact,
          recipientContact: data.recipientContact,
          shipmentDevices,
        };
        await handleCreateShipmentLabel(input);
        if (data.rmaTicket.clinicShipmentReminder?.isReminder) {
          await handleClinicRmaShipmentReminder(data.rmaTicket.id, false);
        }
        navigate(`/rma/${data.rmaTicket.id}`);
      } else {
        const input = {
          ticketId: data.study?.patientReturn?.id,
          ticketType: 'Patient Return',
          rateId: data.rateId,
          type: 'Outbound',
          senderContact: data.senderContact,
          recipientContact: data.recipientContact,
        };
        await handleCreateShipmentLabel(input);
        navigate(`/studies/${data.study?.id}`, {
          state: {
            shouldNavigateToReturnStatusTab: true,
          },
        });
      }
      showMessageSuccess({ content: t`Your purchase was successful!` });
    } catch (error: any) {
      console.error('Failed to create shipping label: ', error);
      showMessageError({ content: error?.message });
      setState({ isSaving: false });
    }
  };

  /**
   * The rate and parcel details displayed in the shipment preview. For a shipping label it is the already selected rate; otherwise it merges the rate matching data.rateId with the name and weight of the parcel configured as packageShipmentLabel.
   *
   * @returns The rate object shown in the preview.
   */
  const selectedRateData = useMemo(() => {
    if (props.isShippingLabel) {
      return props.data?.selectedRate;
    }

    const findedRate = _.find(props.data.rates, { id: props.data?.rateId }) || {};
    const { name, weight } =
      _.find(props.data.shipmentParcels, { id: patientReturnConfig?.packageShipmentLabel }) || {};
    const result = {};
    _.assign(result, findedRate, {
      name,
      weight,
    });
    return result;
  }, [props.data.selectedRate, props.data?.rateId, patientReturnConfig?.packageShipmentLabel]);

  return { state, onSubmit, selectedRateData };
};
